import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTranscript } from './queries';
import {
  buildExcerptDetailQueries,
  combineExcerptDetails,
  reportMissingExcerptDetails,
} from '../excerpts/queries';

// Reactive ids→details resolution for a transcript's excerpts — the
// ids-only replacement for reading a bulk transcript.excerpts array.
//
// One cache-only observer per excerpt (the shared spec in
// buildExcerptDetailQueries — same as useParagraphExcerpts, at transcript
// scale): a coding write to any member's detail entry re-renders the
// consumer with fresh data, and the observers pin the detail entries
// against garbage collection for as long as a consumer is mounted. The
// `combine` output is structurally shared by react-query, so consumers'
// memos see a stable array identity until an entry actually changes.
//
// Mount this once per page and share the result — every instance is its
// own observer set, and observer count is the scale risk the perf gate
// watches (see the ids-only refactor plan).
export function useTranscriptExcerpts(transcriptId) {
  const { data: transcriptData } = useTranscript(transcriptId);
  const queryClient = useQueryClient();

  const excerptIds = useMemo(
    () => (Array.isArray(transcriptData?.excerpt_ids) ? transcriptData.excerpt_ids : []),
    [transcriptData]
  );

  // Memoized on the ids: react-query keys its option-defaulting and key
  // hashing on the queries array's identity, so an inline map here would
  // redo N hashes per consumer render.
  const queries = useMemo(() => buildExcerptDetailQueries(excerptIds), [excerptIds]);
  const excerpts = useQueries({ queries, combine: combineExcerptDetails });

  // A real (numeric) member with no detail entry is an invariant violation
  // (see reportMissingExcerptDetails): report it, render the rest. Keyed on
  // the transcript entry's identity — entries and membership are written
  // together, so that is the only moment a hole can appear.
  useEffect(() => {
    reportMissingExcerptDetails(queryClient, transcriptData);
  }, [transcriptData, queryClient]);

  return { excerpts, excerptIds };
}
